import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DevRequestAttachment } from "../types";
import { devRequestService } from "../services/dataService";
import { DR_ACCEPT, DrPending, drErr, drExtLabel, drLayerBlocked, drMakePending, fmtSize } from "../utils/devRequests";
import "../pages/PurchaseOrders.css";
import "../pages/DevRequests.css";

/**
 * Geliştirme talebi dosyaları:
 *  - DrDropZone: talep formunda bekleyen dosyalar (yapıştır / sürükle-bırak / seç).
 *  - DrAttachmentGrid: çekmecede yüklenmiş dosyalar + yüklenmekte olanlar.
 *  - DrLightbox: görsel/video önizleme (.po-overlay.center.top).
 * API Bearer başlığıyla çalışır: dosyalar blob olarak çekilir, blob: adresiyle gösterilir.
 */

export type Pending = DrPending;
export interface DrUploading { key: string; name: string; progress: number; error?: string }

const I = {
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  play: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l10.5-6.5z" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" /><path d="M5 20h14" /></svg>,
  prev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  next: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
};

/**
 * blob: adresleri sözlüğü. Bileşen gerçekten kapanınca hepsi bırakılır; StrictMode'un
 * geliştirmedeki sahte kapat-aç döngüsünde bırakılmaz (yoksa önizlemeler kırılırdı).
 * `alive` false iken yeni adres üretilmemeli (kapanmış bileşene sızıntı).
 */
const useBlobUrls = () => {
  const urls = useRef<Record<string, string>>({});
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      window.setTimeout(() => {
        if (alive.current) return;
        Object.keys(urls.current).forEach((k) => URL.revokeObjectURL(urls.current[k]));
        urls.current = {};
      }, 0);
    };
  }, []);
  return { urls, alive };
};

const saveUrl = (url: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = url; a.download = fileName; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
};

const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).indexOf("Files") >= 0;

// ───────────────────────────── DrDropZone ─────────────────────────────

export interface DrDropZoneProps {
  files: Pending[];
  onChange: (files: Pending[]) => void;
  disabled?: boolean;
}

/** Talep formundaki ek alanı. Ekleme drMakePending'den geçer (tür/boyut/adet/toplam/aynı dosya); reddedilenler gerekçesiyle listede görünür. */
export const DrDropZone: React.FC<DrDropZoneProps> = ({ files, onChange, disabled }) => {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { urls, alive } = useBlobUrls();
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  // Görsel önizlemeleri bileşen üretir ve sahiplenir: listeden çıkan anında, kalanlar kapanışta bırakılır
  useEffect(() => {
    if (!alive.current) return;
    const cur = urls.current;
    const next: Record<string, string> = {};
    let changed = false;
    files.forEach((p) => {
      if (p.preview || p.rejected || !/^image\//.test(p.file.type)) return;
      if (cur[p.key]) next[p.key] = cur[p.key];
      else { next[p.key] = URL.createObjectURL(p.file); changed = true; }
    });
    Object.keys(cur).forEach((k) => { if (!next[k]) { URL.revokeObjectURL(cur[k]); changed = true; } });
    if (changed) { urls.current = next; setThumbs(next); }
  }, [files, urls, alive]);

  const add = (list: FileList | null | undefined) => {
    if (disabled || !list || !list.length) return;
    onChange(drMakePending(Array.from(list), files));
  };
  const remove = (key: string) => onChange(files.filter((p) => p.key !== key));
  const openDialog = () => { if (!disabled) inputRef.current?.click(); };
  const ok = files.filter((p) => !p.rejected);
  const total = ok.reduce((s, p) => s + p.file.size, 0);

  return (
    <div className="dr-dropwrap">
      <div className={`dr-drop ${over ? "over" : ""} ${disabled ? "disabled" : ""}`} role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled || undefined}
        aria-label="Dosya ekle: ekran görüntüsünü yapıştırın, sürükleyip bırakın ya da dosya seçin"
        onClick={openDialog}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDialog(); } }}
        onDragEnter={(e) => { if (!hasFiles(e)) return; e.preventDefault(); if (!disabled) setOver(true); }}
        onDragOver={(e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = disabled ? "none" : "copy"; if (!disabled) setOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}>
        <span className="dr-drop-icon">{I.upload}</span>
        <span className="dr-drop-text">Ekran görüntüsünü <b>Ctrl+V</b> ile yapıştırın, sürükleyip bırakın ya da <b>dosya seçin</b></span>
        <span className="dr-drop-hint">Ekran görüntüsü için Win+Shift+S · PNG, JPG, PDF, Excel, Word, MP4 · dosya başına 10 MB (video 20 MB) · en fazla 15 dosya</span>
      </div>
      <input ref={inputRef} type="file" multiple accept={DR_ACCEPT} hidden tabIndex={-1} onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {files.length > 0 && (
        <div className="dr-pending" aria-live="polite">
          {files.map((p) => {
            const thumb = p.preview || thumbs[p.key];
            const running = typeof p.progress === "number" && p.progress < 100 && !p.error;
            return (
              <div key={p.key} className={`dr-pending-row ${p.error ? "err" : ""} ${p.progress === 100 ? "done" : ""}`}>
                <span className="dr-pending-thumb">{thumb ? <img src={thumb} alt="" /> : <span className="dr-file-ext">{drExtLabel(p.file.name)}</span>}</span>
                <div className="dr-pending-text">
                  <b title={p.file.name}>{p.file.name}</b>
                  <small>{fmtSize(p.file.size)}{p.error ? ` · ${p.error}` : p.progress === 100 ? " · yüklendi" : running ? ` · %${p.progress}` : ""}</small>
                  {running && <div className="po-progress"><div className="po-progress-bar"><i style={{ width: `${p.progress}%` }} /></div></div>}
                </div>
                <button type="button" className="po-ib danger" aria-label={`${p.file.name} dosyasını çıkar`} title="Çıkar" disabled={disabled} onClick={() => remove(p.key)}>{I.close}</button>
              </div>
            );
          })}
          <div className="dr-pending-total"><b>{ok.length}</b> dosya · <b>{fmtSize(total)}</b></div>
        </div>
      )}
    </div>
  );
};

// ───────────────────────────── DrAttachmentGrid ─────────────────────────────

export interface DrAttachmentGridProps {
  items: DevRequestAttachment[];
  uploading: DrUploading[];
  /** Görsel/video tıklanınca: items içindeki sıra (DrLightbox'a `start` olarak verilir). PDF ve diğerlerini ızgara kendisi açar. */
  onOpen: (index: number) => void;
  /** × düğmesi (yalnız canDelete). Onay sayfada sorulur: "Dosyayı kaldır". */
  onDelete: (a: DevRequestAttachment) => void;
  /** PDF/dosya indirilemediğinde (ör. silinmiş) — sayfa bildirimi için. */
  onError?: (message: string) => void;
  /** Hatalı yükleme kutucuğunu kapatma (verilmezse × çıkmaz). */
  onDismissUpload?: (key: string) => void;
  /** Hiç dosya yokken gösterilen satır; "" verilirse hiçbir şey çizilmez. */
  emptyText?: string;
}

const THUMB_MAX = 3 * 1024 * 1024, THUMB_LIMIT = 12;
const OPEN_HINT: Record<DevRequestAttachment["kind"], string> = { image: "büyüt", video: "oynat", pdf: "yeni sekmede aç", file: "indir" };

export const DrAttachmentGrid: React.FC<DrAttachmentGridProps> = ({ items, uploading, onOpen, onDelete, onError, onDismissUpload, emptyText = "Henüz dosya yok." }) => {
  const { urls, alive } = useBlobUrls();
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const itemsRef = useRef(items);
  const failed = useRef<Record<string, true>>({});
  const pumping = useRef(false);

  // Küçük resimler sırayla (aynı anda tek istek), en çok 12 tane; çekmece açık kaldıkça önbellekte kalır
  const pump = useCallback(async () => {
    if (pumping.current) return;
    pumping.current = true;
    try {
      for (;;) {
        if (!alive.current || Object.keys(urls.current).length >= THUMB_LIMIT) break;
        const next = itemsRef.current.find((a) => a.kind === "image" && a.size <= THUMB_MAX && !urls.current[a.attachmentId] && !failed.current[a.attachmentId]);
        if (!next) break;
        try {
          const blob = await devRequestService.attachmentBlob(next.attachmentId);
          if (!alive.current) break;
          urls.current = { ...urls.current, [next.attachmentId]: URL.createObjectURL(blob) };
          setThumbs(urls.current);
        } catch { failed.current[next.attachmentId] = true; }
      }
    } finally { pumping.current = false; }
  }, [urls, alive]);
  useEffect(() => { itemsRef.current = items; pump(); }, [items, pump]);

  const open = async (a: DevRequestAttachment, i: number) => {
    if (a.kind === "image" || a.kind === "video") return onOpen(i);
    if (busyId) return;
    // Sekme tıklama anında açılır: indirme sürerse açılır pencere engeli devreye girmesin
    const w = a.kind === "pdf" ? window.open("", "_blank") : null;
    setBusyId(a.attachmentId);
    try {
      const blob = await devRequestService.attachmentBlob(a.attachmentId);
      const url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: a.contentType }));
      if (a.kind === "pdf") { if (w) w.location.href = url; else window.open(url, "_blank"); }
      else saveUrl(url, a.fileName);
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      w?.close();
      onError?.(drErr(e, "Dosya açılamadı."));
    } finally { setBusyId(null); }
  };

  if (!items.length && !uploading.length) return emptyText ? <p className="po-hint dr-files-empty">{emptyText}</p> : null;
  return (
    <div className="dr-files">
      {items.map((a, i) => {
        const thumb = thumbs[a.attachmentId];
        return (
          <div key={a.attachmentId} className={`dr-file ${busyId === a.attachmentId ? "busy" : ""}`}>
            <button type="button" className="dr-file-open" title={`${a.fileName} · ${OPEN_HINT[a.kind]}`} aria-label={`${a.fileName}, ${OPEN_HINT[a.kind]}`} onClick={() => open(a, i)}>
              <span className="dr-file-thumb">
                {thumb ? <img src={thumb} alt="" /> : <span className={`dr-file-ext ${a.kind}`}>{drExtLabel(a.fileName)}</span>}
                {a.kind === "video" && <span className="dr-file-play" aria-hidden="true">{I.play}</span>}
              </span>
              <span className="dr-file-meta">
                <b title={a.fileName}>{a.fileName}</b>
                <small>{busyId === a.attachmentId ? "Açılıyor…" : `${fmtSize(a.size)} · ${a.uploadedByName}`}</small>
              </span>
            </button>
            {a.canDelete && <button type="button" className="po-ib danger dr-file-del" aria-label={`${a.fileName} dosyasını kaldır`} title="Kaldır" onClick={() => onDelete(a)}>{I.close}</button>}
          </div>
        );
      })}
      {uploading.map((u) => (
        <div key={u.key} className={`dr-file uploading ${u.error ? "err" : ""}`}>
          <span className="dr-file-thumb"><span className="dr-file-ext">{drExtLabel(u.name)}</span></span>
          <span className="dr-file-meta">
            <b title={u.name}>{u.name}</b>
            <small role={u.error ? "alert" : undefined}>{u.error ? u.error : `Yükleniyor · %${u.progress}`}</small>
          </span>
          {!u.error && <span className="dr-file-progress"><i style={{ width: `${u.progress}%` }} /></span>}
          {u.error && onDismissUpload && <button type="button" className="po-ib dr-file-del" aria-label={`${u.name} kutucuğunu kapat`} title="Kapat" onClick={() => onDismissUpload(u.key)}>{I.close}</button>}
        </div>
      ))}
    </div>
  );
};

// ───────────────────────────── DrLightbox ─────────────────────────────

export interface DrLightboxProps {
  /** Talebin tüm dosyaları; yalnız görsel/videolar arasında gezinilir. */
  items: DevRequestAttachment[];
  /** Açılacak dosyanın items içindeki sırası (DrAttachmentGrid onOpen'dan gelen). */
  start: number;
  onClose: () => void;
}

export const DrLightbox: React.FC<DrLightboxProps> = ({ items, start, onClose }) => {
  const visual = items.filter((a) => a.kind === "image" || a.kind === "video");
  const [id, setId] = useState<string | undefined>(() => (items[start] && (items[start].kind === "image" || items[start].kind === "video") ? items[start].attachmentId : visual[0]?.attachmentId));
  const idx = Math.max(0, visual.findIndex((a) => a.attachmentId === id));
  const cur = visual[idx] as DevRequestAttachment | undefined;
  const { urls, alive } = useBlobUrls();
  const [shown, setShown] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Açan öğe ilk render'da (odak buraya geçmeden) yakalanır
  const [opener] = useState(() => document.activeElement as HTMLElement | null);

  const go = (d: number) => { if (visual.length > 1) setId(visual[(idx + d + visual.length) % visual.length].attachmentId); };

  // Gösterilen dosya listeden düşerse ilk görsele geçilir; hiç görsel kalmadıysa önizleme kapanır
  useEffect(() => { if (!cur) onClose(); }, [cur, onClose]);

  const curId = cur?.attachmentId;
  useEffect(() => {
    if (!curId || urls.current[curId]) return;
    devRequestService.attachmentBlob(curId)
      .then((blob) => {
        if (!alive.current) return;
        if (!urls.current[curId]) urls.current = { ...urls.current, [curId]: URL.createObjectURL(blob) };
        setShown(urls.current);
      })
      .catch((e) => { if (alive.current) setErrors((m) => ({ ...m, [curId]: drErr(e, "Dosya açılamadı.") })); });
  }, [curId, urls, alive]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (drLayerBlocked(overlayRef.current)) return;
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && visual.length > 1) {
        // Video denetimlerinde oklar ileri/geri sarar; orada gezinmeyi ele geçirme
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === "VIDEO" || t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
        e.preventDefault(); e.stopPropagation(); go(e.key === "ArrowLeft" ? -1 : 1);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // autoFocus yerine efekt: StrictMode geliştirmede geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => { closeRef.current?.focus({ preventScroll: true }); }, []);

  if (!cur) return null;
  const url = shown[cur.attachmentId];
  const error = errors[cur.attachmentId];
  return createPortal(
    <div className="po-overlay center top dr-lightbox-overlay" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dr-lightbox" role="dialog" aria-modal="true" aria-label={`${cur.fileName} önizleme`}>
        <div className="dr-lightbox-stage">
          {url
            ? cur.kind === "video"
              ? <video key={cur.attachmentId} src={url} controls autoPlay={false} playsInline preload="metadata" />
              : <img key={cur.attachmentId} src={url} alt={cur.fileName} />
            : error
              ? <div className="dr-lightbox-msg" role="alert">{error}</div>
              : <div className="dr-lightbox-msg"><span className="po-spin" /> Yükleniyor…</div>}
          {visual.length > 1 && <>
            <button type="button" className="dr-lightbox-nav prev" aria-label="Önceki dosya" onClick={() => go(-1)}>{I.prev}</button>
            <button type="button" className="dr-lightbox-nav next" aria-label="Sonraki dosya" onClick={() => go(1)}>{I.next}</button>
          </>}
        </div>
        <div className="dr-lightbox-bar">
          <span className="dr-lightbox-cap" title={cur.fileName}><b>{cur.fileName}</b> · {fmtSize(cur.size)}{visual.length > 1 && <span className="dr-lightbox-pos"> · {idx + 1}/{visual.length}</span>}</span>
          <button type="button" className="po-btn small" disabled={!url} onClick={() => url && saveUrl(url, cur.fileName)}>{I.download} İndir</button>
          <button type="button" ref={closeRef} className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
